import { WIDGET_KEYS } from "../../../other/constants/WidgetKeys.jsx";
import MainEditorBase from "../base/MainEditorBase.jsx";
import ResourceGeneralTab from "./ResourceGeneralTab.jsx";
import ResourceRemindersTab from "./ResourceRemindersTab.jsx";
import ResourceChecklistTab from "./ResourceChecklistTab.jsx";
import ResourceTaskHistoryTab from "./ResourceTaskHistoryTab.jsx";
import {PERSISTENCE_STATE_KEYS} from "../../../other/constants/PersistenceStateKeys.jsx";
import {DEEP_LINK_URL_KEYS, EDITOR_CONTEXT_MENU_ITEM_KEYS} from "../../../other/constants/OtherKeys.jsx";
import {getEntityShareLink} from "../../../other/utils/otherUtils.jsx";
import {canCopyRecord} from "../../../other/utils/editorUtils.jsx";
import ResourceNotifyTab from "./ResourceNotifyTab.jsx";
import ResourceNotesTab from "./ResourceNotesTab.jsx";
import ResourceContactTab from "./ResourceContactTab.jsx";
import ResourceFilesTab from "./ResourceFilesTab.jsx";
import { SwivuModernEditorMixin } from "../mixin/SwivuModernEditorMixin.jsx";
import { ENTITY_TYPES } from "@/other/constants/OtherKeys.jsx";

export default class SwivuResourceEditorV2 extends SwivuModernEditorMixin(MainEditorBase) {
  static get $name() {
    return "SwivuResourceEditorV2";
  }

  static get type() {
    return WIDGET_KEYS.resourceEditorV2Type;
  }

  static get configurable() {
    return {
      ...super.configurable,
      title: "New Resource",
      minWidth: "70em",
      maxWidth: "100vw",
      width: "93em",
      minHeight: "600px",
      stateId: PERSISTENCE_STATE_KEYS.resourceEditorV2,
      maxHeight: "100vh",
      height: "min(100vh, 800px)",
      closable: true,
      autoClose: false,
      draggable: true,
      defaultFocus: (w) => w.ref === "nameField",
      // Set this to true to update record immediately as task editor field changes
      autoUpdateRecord: true,
      items: {
        tabs: {
          type: WIDGET_KEYS.swivuTabPanel,
          stateId: "swivu-resource-editor-tab-v2",
          contentElementCls: "task-editor-content",
          listeners: {
            beforeTabChange: "up.onTabChangeHandler"
          },
          items: {
            generalTab: { type: ResourceGeneralTab.type, },
            notifyTab: { type: ResourceNotifyTab.type, swivuDisableOnPhantomRecord: true, },
            contactTab: { type: ResourceContactTab, swivuDisableOnPhantomRecord: true, },
            filesTab: { type: ResourceFilesTab.type, swivuDisableOnPhantomRecord: true },
            notesTab: { type: ResourceNotesTab.type, swivuDisableOnPhantomRecord: true, },
            remindersTab: { type: ResourceRemindersTab.type, swivuDisableOnPhantomRecord: true, },
            checklistTab: { type: ResourceChecklistTab.type, swivuDisableOnPhantomRecord: true, },
            taskHistoryTab: { type: ResourceTaskHistoryTab.type, swivuDisableOnPhantomRecord: true, },
            entityPageTab: {
              title: "Dashboard",
              swivuDisableOnPhantomRecord: true,
              tab: {
                icon: "fa-regular fa-house",
              },
            }
          }
        }
      },
      listeners: {
        hide: "handleHide"
      },
      bbar: {
        items: {
          dateMetaDataField: {
            type: "label",
          }
        }
      }
    };
  }

  editRecord(record, context, otherParams) {
    super.editRecord(record, context, otherParams);
    this.swivuRecord = record;
    // The reason for assigning project to record is that, in models we get access to project
    // instance from this.project that HasProjectMixin assigns from this.firstStore.crudManager
    // but when there's a new record it does not have firstStore available, so we need to assign the project to it respectively.
    // not doing this you will errors in browser console
    if (record.isPhantom) record.project = context.project;
    this.updateEditorTitle(record.name, record);
    const editorTabs = this.widgetMap.tabs;
    const isDemoUser = context.user?.isDemoUser;
    this.loadContextMenu(record, context, isDemoUser, editorTabs);
    this.widgetMap.tabs.eachWidget((widget) => {
      if (widget.isResourceEditTabBase) {
        if (otherParams.note && widget.type === ResourceNotesTab.type) {
          widget.editRecord(record, context, { ...otherParams, editor: this, note: otherParams.note });
        } else {
          widget.editRecord(record, context, { ...otherParams, editor: this });
        }
      }
    }, false);

    if (otherParams.isFirstShow) this.initializeModernEditor();
  }

  handleHide() {
    this.destroyTabCheckListener();
    //Implementing this logic on the case when user opens dialog for deleting record but cancels it and closes the editor
    if (this.removeListenerAttached) this.removeListenerAttached();
  }

  loadContextMenu(record, context, isDemoUser, editorTabs) {
    const recordType = "resource";
    this.configureEditorContextMenu(record, context, {
      recordStore: context.project.resourceStore,
      items: {
        // View Resource's Agenda is temporarily disabled
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.viewAgendaItem]: { enabled: false },
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.archiveItem]: { enabled: true },
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.deleteItem]: { enabled: true },
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.shareItem]: {
          enabled: !isDemoUser,
          editorParams() {
            return {
              message: getEntityShareLink({ record, context, editorTabs, entityType: DEEP_LINK_URL_KEYS.resource, entityDetail: record }),
            }
          }
        },
        [EDITOR_CONTEXT_MENU_ITEM_KEYS.copyItem]: {
          enabled: canCopyRecord(!isDemoUser, context.selectedBoard, context.boardManager.boardNameStore),
          recordType,
        },
      },
    });
  }

  onTabChangeHandler({ activeItem, source }) {
    if (activeItem.ref === "entityPageTab") {
      const { swivuRecord, context } = this;
      context.entityManager.openEntityManager({
        entityType: ENTITY_TYPES.RESOURCE,
        entityId: swivuRecord.id
      });
      setTimeout(() => {
        source.activeTab = 0;
        this.hide();
      })
    }
  }
}

SwivuResourceEditorV2.initClass();
